import type { FeedbackAlertProps } from '@/components';
import { useConfigStore } from '@/stores/config.ts';
import { mapEnvironmentToConfig } from '@/stores/config/mapEnvironmentToConfig.ts';

/** Props for {@link FeedbackAlert} when runtime `GET /config` fails (used by app shell gates). */
export const configLoadFailedFeedbackProps = (
  error: string
): Pick<FeedbackAlertProps, 'text' | 'variant' | 'duration'> => ({
  text: `Could not load server configuration (${error}). Try refreshing the page.`,
  variant: 'alarm',
  duration: 86_400_000,
});

let configInitInFlight: Promise<void> | null = null;
/** True after `/config` has finished (success or non-abort failure); reset only on full reload. */
let configInitSettled = false;

/**
 * Loads runtime `/config` into the store. Call once from the app entry (e.g. `main.tsx`).
 * Dedupes concurrent calls; after a settled run, further calls resolve immediately without refetching.
 * Pass `signal` to cancel (e.g. teardown); abort resets `initStarted` so a later call can retry.
 */
export function initConfigStore(options?: { signal?: AbortSignal }): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if (configInitSettled) {
    return Promise.resolve();
  }
  if (configInitInFlight) {
    return configInitInFlight;
  }

  configInitInFlight = (async () => {
    useConfigStore.setState(s => ({ ...s, initStarted: true, error: null }));

    try {
      const response = await fetch(`${window.origin}/config`, { signal: options?.signal });
      if (!response.ok) {
        const bodySnippet = await response
          .text()
          .then(t => t.trim().slice(0, 300))
          .catch(() => '');
        const suffix = bodySnippet ? ` — ${bodySnippet}` : '';
        throw new Error(`GET /config failed: ${response.status} ${response.statusText}${suffix}`);
      }
      const cfg = mapEnvironmentToConfig(await response.json());
      console.info(
        `[/config] ${new Date().toLocaleTimeString()} read at startup: image_tag ${cfg.imageTag}`
      );
      useConfigStore.setState(s => ({
        ...s,
        ...cfg,
        loaded: true,
        initStarted: false,
        error: null,
      }));
      configInitSettled = true;
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') {
        useConfigStore.setState(s => ({ ...s, initStarted: false }));
        throw err;
      }
      console.error('Init /config failed:', err);
      useConfigStore.setState(s => ({
        ...s,
        loaded: true,
        initStarted: false,
        error: String(err),
      }));
      configInitSettled = true;
    } finally {
      configInitInFlight = null;
    }
  })();

  return configInitInFlight;
}
